import { createTheme } from '@mui/material/styles';

// Custom color palette with neutral tones
const colors = {
  primary: {
    main: '#64748B', // Slate grey
    light: '#94A3B8',
    dark: '#475569',
    contrastText: '#ffffff'
  },
  secondary: {
    main: '#6B7280', // Cool grey
    light: '#9CA3AF',
    dark: '#4B5563',
    contrastText: '#ffffff'
  },
  neutral: {
    main: '#64748B',
    light: '#94A3B8',
    dark: '#475569',
    contrastText: '#ffffff'
  },
  background: {
    default: '#ffffff',
    paper: '#f8fafc'
  },
  text: {
    primary: '#1e293b',
    secondary: '#64748b'
  },
  divider: '#e2e8f0'
};

// Dark mode colors
const darkColors = {
  ...colors,
  background: {
    default: '#0f172a',
    paper: '#1e293b'
  },
  text: {
    primary: '#f8fafc',
    secondary: '#cbd5e1'
  },
  divider: '#334155',
  action: {
    active: '#f8fafc',
    hover: 'rgba(248, 250, 252, 0.08)'
  }
};

// Custom typography using Inter font
const typography = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  h1: {
    fontWeight: 600,
    fontSize: '2.5rem',
    lineHeight: 1.2
  },
  h2: {
    fontWeight: 600,
    fontSize: '2rem',
    lineHeight: 1.2
  },
  h3: {
    fontWeight: 600,
    fontSize: '1.75rem',
    lineHeight: 1.2
  },
  h4: {
    fontWeight: 600,
    fontSize: '1.5rem',
    lineHeight: 1.2
  },
  h5: {
    fontWeight: 600,
    fontSize: '1.25rem',
    lineHeight: 1.2
  },
  h6: {
    fontWeight: 600,
    fontSize: '1rem',
    lineHeight: 1.2
  },
  body1: {
    fontSize: '1rem',
    lineHeight: 1.5
  },
  body2: {
    fontSize: '0.875rem',
    lineHeight: 1.5
  },
  button: {
    textTransform: 'none',
    fontWeight: 500
  }
};

// Create base theme configuration
const baseTheme = {
  typography,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          transition: 'all 0.2s ease-in-out'
        },
        contained: {
          background: `linear-gradient(45deg, ${colors.primary.main} 30%, ${colors.primary.light} 90%)`,
          '&:hover': {
            background: `linear-gradient(45deg, ${colors.primary.dark} 30%, ${colors.primary.main} 90%)`
          }
        },
        text: {
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          }
        },
        outlined: {
          '&:hover': {
            borderColor: colors.primary.main,
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          }
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            transition: 'all 0.2s ease-in-out',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.divider
            },
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.primary.main
              }
            },
            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.primary.main
              }
            }
          },
          '& .MuiInputLabel-root': {
            color: colors.text.secondary
          },
          '& .MuiInputBase-input': {
            color: colors.text.primary
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          '&.MuiChip-filled': {
            backgroundColor: 'rgba(100, 116, 139, 0.1)',
            color: colors.primary.main
          }
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: colors.divider
        }
      }
    },
    MuiTimelineDot: {
      styleOverrides: {
        root: {
          backgroundColor: colors.primary.main,
          '&:hover': {
            backgroundColor: colors.primary.dark
          }
        }
      }
    },
    MuiTimelineConnector: {
      styleOverrides: {
        root: {
          backgroundColor: colors.divider
        }
      }
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          height: 64
        }
      }
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease-in-out',
          '&.Mui-selected': {
            '& .MuiBottomNavigationAction-label': {
              fontWeight: 500
            }
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          }
        },
        label: {
          fontSize: '0.75rem',
          '&.Mui-selected': {
            fontSize: '0.75rem'
          }
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: darkColors.background.paper,
          paddingRight: 'env(safe-area-inset-right)'
        }
      }
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 64,
          paddingLeft: 16,
          paddingRight: 16,
          '@media (min-width: 600px)': {
            paddingLeft: 24,
            paddingRight: 24
          },
          '& > *:last-child': {
            marginRight: 8
          }
        }
      }
    }
  }
};

// Create and export the default theme (light mode)
const theme = createTheme({
  ...baseTheme,
  palette: colors
});

// Export function to get theme with specified mode
export const getTheme = (mode) =>
  createTheme({
    ...baseTheme,
    palette: mode === 'dark' ? darkColors : colors,
    components: {
      ...baseTheme.components,
      MuiButton: {
        styleOverrides: {
          ...baseTheme.components.MuiButton.styleOverrides,
          text: {
            color: mode === 'dark' ? darkColors.text.primary : colors.text.primary,
            '&:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(248, 250, 252, 0.08)' : 'rgba(0, 0, 0, 0.04)'
            }
          },
          outlined: {
            borderColor: mode === 'dark' ? darkColors.divider : colors.divider,
            color: mode === 'dark' ? darkColors.text.primary : colors.text.primary,
            '&:hover': {
              borderColor: mode === 'dark' ? darkColors.text.primary : colors.primary.main,
              backgroundColor: mode === 'dark' ? 'rgba(248, 250, 252, 0.08)' : 'rgba(0, 0, 0, 0.04)'
            }
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: mode === 'dark' ? darkColors.text.primary : colors.text.primary,
            '&:hover': {
              transform: 'scale(1.15)',
              backgroundColor: mode === 'dark' ? 'rgba(248, 250, 252, 0.08)' : 'rgba(0, 0, 0, 0.04)',
              color: mode === 'dark' ? darkColors.text.primary : colors.primary.main
            },
            '&.Mui-selected': {
              color: mode === 'dark' ? darkColors.text.primary : colors.primary.main
            }
          }
        }
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            color: mode === 'dark' ? darkColors.text.secondary : colors.text.secondary,
            borderColor: mode === 'dark' ? darkColors.divider : colors.divider,
            '&.Mui-selected': {
              backgroundColor: mode === 'dark' ? 'rgba(248, 250, 252, 0.08)' : 'rgba(100, 116, 139, 0.08)',
              color: mode === 'dark' ? darkColors.text.primary : colors.primary.main,
              '&:hover': {
                backgroundColor: mode === 'dark' ? 'rgba(248, 250, 252, 0.12)' : 'rgba(100, 116, 139, 0.12)'
              }
            },
            '&:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(248, 250, 252, 0.04)' : 'rgba(100, 116, 139, 0.04)'
            }
          }
        }
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? darkColors.background.paper : colors.background.paper
          }
        }
      },
      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? darkColors.background.paper : colors.background.paper,
            borderTop: `1px solid ${mode === 'dark' ? darkColors.divider : colors.divider}`
          }
        }
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            color: mode === 'dark' ? darkColors.text.secondary : colors.text.secondary,
            '&.Mui-selected': {
              color: mode === 'dark' ? darkColors.text.primary : colors.primary.main
            },
            '&:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(248, 250, 252, 0.08)' : 'rgba(0, 0, 0, 0.04)'
            }
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              transition: 'all 0.2s ease-in-out',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)'
              },
              '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'
                }
              },
              '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.75)' : colors.primary.main
                }
              }
            },
            '& .MuiInputLabel-root': {
              color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'
            },
            '& .MuiInputBase-input': {
              color: mode === 'dark' ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)'
            }
          }
        }
      }
    }
  });

export default theme;
